import { ReactElement, useEffect, useState } from 'react'

import Cookies from 'js-cookie'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { AppCookie } from 'utils/cookies'
import { idIsInVar } from 'utils/featureFlag'
import { getPaymentsDomain } from 'utils/window'

const ErrorLayout = dynamic(() => import('@/layouts/ErrorLayout'))
import { useCompanyAndInvoiceContext } from '@/hooks/providers/CompanyAndInvoiceProvider'

/**
 * Boundary used to make sure partner
 * is enabled for using financing link,
 * and if not on their domain, redirect to it
 */

const PartnerBoundary = ({ children }: { children: ReactElement }) => {
  const [enabled, setEnabled] = useState(true)

  const { asPath } = useRouter()
  const { company, error, loading } = useCompanyAndInvoiceContext()

  const token = Cookies.get(AppCookie.TokenCustomer)
  const refreshToken = Cookies.get(AppCookie.RefreshCustomer)
  const customerId = Cookies.get(AppCookie.CustomerId)

  useEffect(() => {
    if (!company) return

    const maintenanceModePage = '/under-maintenance'
    if (
      // eslint-disable-next-line alternative/no-invalid-env-usage
      idIsInVar(company?.id, process.env.NEXT_PUBLIC_MAINTENANCE_MODE) &&
      window.location.pathname !== maintenanceModePage
    ) {
      // Safe: maintenanceModePage is a hardcoded string literal ('/under-maintenance')
      window.location.href = maintenanceModePage
    }
  }, [company])

  useEffect(() => {
    if (error) setEnabled(false)

    const isPartnerActive = !!company?.paymentsPortalUrl

    if (company === null || !isPartnerActive) {
      setEnabled(false)
    }

    // if partner has custom portal domain but the user is not on it, redirect
    const currentDomain = getPaymentsDomain()
    const useCustomUrl =
      !!company?.paymentsPortalUseCustomUrl &&
      !!company?.paymentsPortalCustomUrl
    const expectedDomain = useCustomUrl
      ? company?.paymentsPortalCustomUrl
      : company?.paymentsPortalUrl
    // Both the default and the verified custom URL are accepted hosts —
    // partners with a custom URL also keep the default reachable.
    const isOnAcceptedDomain =
      currentDomain === company?.paymentsPortalUrl ||
      (useCustomUrl && currentDomain === company?.paymentsPortalCustomUrl)

    if (
      company?.paymentsPortalEnabled &&
      currentDomain &&
      expectedDomain &&
      !isOnAcceptedDomain
    ) {
      // Safe (protocol): hardcoded https:// prefix. Note: token exposure via URL params is tracked separately (alt-front-end-vod)
      window.location.href = `https://${expectedDomain}/custom-portal?token=${token}&refreshToken=${refreshToken}&customerId=${customerId}&redirect=${asPath}`
    }
    return () => setEnabled(true)
  }, [company, error])

  if (enabled && company) return <>{children}</>

  if (!enabled && !loading) {
    return <ErrorLayout />
  }
  return null
}

export default PartnerBoundary
